import React from "react";
import Button from "@mui/material/Button";
import RightworkSelect from "../../../../../components/shared/RightworkSelect";
import EmployeeProfileSectionCard from "./EmployeeProfileSectionCard";
import { postRightworkAPI } from "../../utilities/requests";
import { API_post_store_team_member_clock_in_expectation } from "../../../../../constants";

const isSalaryType = (wageType) =>
  ["salary", "salaried"].includes(String(wageType || "").trim().toLowerCase());

// Mirrors user_info.clock_in: optional by default exactly when exempt.
export const defaultClockInOptional = (member) =>
  member?.labor_exempt ?? isSalaryType(member?.wage_type);

const VALUE_FOR = { default: null, required: "required", optional: "optional" };

export default function EmployeeTimekeepingSection({ selectedTeamMemberData: member, homeStore, fetchStoreTeamMember }) {
  const id = React.useId();
  const stored = member?.clock_in_expectation || "default";
  // The saved value: the prop, or what this form last saved (the member
  // refresh that updates the prop runs in the background).
  const [baseline, setBaseline] = React.useState(stored);
  const [value, setValue] = React.useState(stored);
  const [saving, setSaving] = React.useState(false);
  const [status, setStatus] = React.useState(null); // "saved" | error message

  // A refreshed saved value replaces the field only while it has no edit
  // (or for a different employee), so a refresh landing mid-edit keeps it.
  const lastBaseline = React.useRef(stored);
  const lastMember = React.useRef(member?.id);
  React.useEffect(() => {
    const memberChanged = lastMember.current !== member?.id;
    setValue((current) => (memberChanged || current === lastBaseline.current ? stored : current));
    setBaseline(stored);
    lastBaseline.current = stored;
    lastMember.current = member?.id;
  }, [member?.id, stored]);

  const dirty = value !== baseline;

  const submit = async (event) => {
    event.preventDefault();
    if (!dirty || saving) return;
    setSaving(true);
    setStatus(null);
    try {
      await postRightworkAPI(API_post_store_team_member_clock_in_expectation, {
        store_id: homeStore,
        store_team_member_id: member.id,
        clock_in_expectation: VALUE_FOR[value],
      });
      setBaseline(value);
      lastBaseline.current = value;
      setStatus("saved");
      fetchStoreTeamMember?.(false, member.id);
    } catch (error) {
      setStatus("Clock-in expectations could not be saved. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const defaultLabel = defaultClockInOptional(member) ? "clock-in optional" : "clock-in required";

  return (
    <EmployeeProfileSectionCard header="Timekeeping" bodyClassName="p-0">
      <form onSubmit={submit} noValidate>
        <div className="px-6 py-5">
          <label id={`${id}-label`} htmlFor={id} className="mb-2 block text-sm font-medium text-slate-900">
            Clock-in expectations
          </label>
          <div className="max-w-sm">
            <RightworkSelect
              id={id}
              value={value}
              onChange={(event) => { setStatus(null); setValue(event.target.value); }}
              disabled={saving}
              describedBy={`${id}-help`}
              options={[
                { value: "default", label: `Use default: ${defaultLabel}` },
                { value: "required", label: "Clock-in required" },
                { value: "optional", label: "Clock-in optional" },
              ]}
            />
          </div>
          <p id={`${id}-help`} className="mt-2 text-sm leading-5 text-slate-500">
            Controls missing-shift alerts. Employees can still record time when clock-in is optional.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          {status === "saved" && <p role="status" className="mr-auto text-sm text-emerald-700">Clock-in expectations saved.</p>}
          {status && status !== "saved" && <p role="alert" className="mr-auto text-sm text-red-700">{status}</p>}
          <Button type="submit" variant="contained" disableElevation disabled={!dirty || saving}
            sx={{ fontFamily: "inherit", textTransform: "none", borderRadius: "8px", paddingX: "20px", paddingY: "8px", backgroundColor: "#6d28d9", "&:hover": { backgroundColor: "#5b21b6" } }}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </form>
    </EmployeeProfileSectionCard>
  );
}
